'use client';

import {
    faChevronLeft,
    faChevronRight,
    faFileLines,
    faMagnifyingGlassMinus,
    faMagnifyingGlassPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useResizeObserver from '@hooks/useResizeObserver';
import { Button, EmptyState, Spinner } from '@ui';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';

// react-pdf pulls in a worker and a chunk of pdf.js — neither belongs in a server render.
const PdfDocument = dynamic(
    async () => {
        // eslint-disable-next-line
        const module = await import('react-pdf');
        const workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
        module.pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
        return module.Document;
    },
    { ssr: false }
);

const PdfPage = dynamic(
    async () => {
        // eslint-disable-next-line
        const module = await import('react-pdf');
        return module.Page;
    },
    { ssr: false }
);

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;

interface Props {
    /** A file picked in the browser. Wins over `url` — it needs no round trip to show. */
    file?: File | null;
    /** Endpoint streaming the stored document; fetched with credentials into a blob. */
    url?: string | null;
    /** Known up front for a local file; otherwise read off the fetched blob. */
    mimeType?: string | null;
    className?: string;
}

/**
 * The document beside the form: PDF with zoom and page navigation, or a plain image.
 *
 * Lifted out of InvoiceExternalImportForm so the capture stack and the edit dialog show the same
 * thing. It owns its object URLs and revokes them, which is why it takes a File or a URL rather
 * than a ready-made blob URL from the caller.
 */
const DocumentPreview = ({ file, url, mimeType, className }: Props) => {
    const { t } = useTranslation('invoice');
    const [containerRef, { width }] = useResizeObserver<HTMLDivElement>();

    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [resolvedType, setResolvedType] = useState<string | null>(mimeType ?? null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const [numPages, setNumPages] = useState(0);
    const [page, setPage] = useState(1);
    const [pageWidth, setPageWidth] = useState<number | null>(null);
    const [zoom, setZoom] = useState(1);

    // A local file needs no network at all.
    useEffect(() => {
        if (!file) return;

        const next = URL.createObjectURL(file);
        setObjectUrl(next);
        setResolvedType(file.type || mimeType || null);
        setHasError(false);
        setPage(1);

        return () => URL.revokeObjectURL(next);
    }, [file, mimeType]);

    // Stored document: fetched rather than linked, so the blob survives the credentialed request
    // and react-pdf never has to re-request it per page.
    useEffect(() => {
        if (file || !url) return;

        const controller = new AbortController();
        let created: string | undefined;

        setIsLoading(true);
        setHasError(false);
        setPage(1);

        void (async () => {
            try {
                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    signal: controller.signal
                });
                if (!response.ok) throw new Error(String(response.status));

                const blob = await response.blob();
                created = URL.createObjectURL(blob);
                setObjectUrl(created);
                setResolvedType(blob.type || mimeType || null);
            } catch (error: any) {
                if (error?.name !== 'AbortError') setHasError(true);
            } finally {
                setIsLoading(false);
            }
        })();

        return () => {
            controller.abort();
            if (created) URL.revokeObjectURL(created);
        };
    }, [file, url, mimeType]);

    const isImage = resolvedType?.startsWith('image/') ?? false;

    // Fit the page to the container, then apply the user's zoom on top.
    const scale = useMemo(() => {
        if (!pageWidth || !width) return undefined;
        return (Math.floor(width) / pageWidth) * zoom;
    }, [pageWidth, width, zoom]);

    const changeZoom = useCallback(
        (delta: number) => setZoom((prev) => Number(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta)).toFixed(2))),
        []
    );

    const hasNothingToShow = !objectUrl && !isLoading && !hasError;

    return (
        <div className={cn('tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-3', className)}>
            {!isImage && objectUrl && (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-2">
                    {numPages > 1 ? (
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Button
                                size="xs"
                                color="light"
                                isDisabled={page <= 1}
                                onPress={() => setPage((prev) => Math.max(1, prev - 1))}
                                icon={<FontAwesomeIcon icon={faChevronLeft} />}
                            />
                            <span className="tw-text-xs tw-tabular-nums tw-text-accents-6">
                                {t('belegAssistant.preview.page', { page, total: numPages })}
                            </span>
                            <Button
                                size="xs"
                                color="light"
                                isDisabled={page >= numPages}
                                onPress={() => setPage((prev) => Math.min(numPages, prev + 1))}
                                icon={<FontAwesomeIcon icon={faChevronRight} />}
                            />
                        </div>
                    ) : (
                        <span />
                    )}

                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Button
                            size="xs"
                            color="light"
                            onPress={() => changeZoom(-ZOOM_STEP)}
                            icon={<FontAwesomeIcon icon={faMagnifyingGlassMinus} />}
                        />
                        <span className="tw-min-w-[48px] tw-text-center tw-text-xs tw-tabular-nums tw-text-accents-6">
                            {Math.round(zoom * 100)}%
                        </span>
                        <Button
                            size="xs"
                            color="light"
                            onPress={() => changeZoom(ZOOM_STEP)}
                            icon={<FontAwesomeIcon icon={faMagnifyingGlassPlus} />}
                        />
                    </div>
                </div>
            )}

            <div className="tw-min-h-0 tw-flex-1 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-1">
                <div
                    className="tw-scrollbar-thumb-[#c4c4c4] tw-scrollbar-track-transparent tw-h-full tw-w-full tw-overflow-auto tw-p-3"
                    style={{ scrollbarGutter: 'stable', scrollbarWidth: 'thin' }}
                >
                    <div ref={containerRef}>
                        {isLoading && (
                            <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
                                <Spinner />
                            </div>
                        )}

                        {!isLoading && hasError && (
                            <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
                                <EmptyState
                                    size="sm"
                                    icon={faFileLines}
                                    title={t('belegAssistant.preview.loadError')}
                                />
                            </div>
                        )}

                        {!isLoading && hasNothingToShow && (
                            <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
                                <EmptyState size="sm" icon={faFileLines} title={t('belegAssistant.preview.empty')} />
                            </div>
                        )}

                        {!isLoading && !hasError && objectUrl && isImage && (
                            // A blob URL, so next/image would only add a proxy hop it cannot optimise anyway.
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={objectUrl}
                                alt={t('belegAssistant.preview.alt')}
                                className="tw-mx-auto tw-h-full tw-w-full tw-object-contain"
                            />
                        )}

                        {!isLoading && !hasError && objectUrl && !isImage && (
                            <PdfDocument
                                file={objectUrl}
                                loading={
                                    <div className="tw-py-6 tw-text-center tw-text-sm tw-text-accents-6">
                                        {t('belegAssistant.preview.loading')}
                                    </div>
                                }
                                error={
                                    <div className="tw-py-6 tw-text-center tw-text-sm tw-text-error">
                                        {t('belegAssistant.preview.loadError')}
                                    </div>
                                }
                                onLoadSuccess={({ numPages: loaded }) => setNumPages(loaded)}
                                onLoadError={() => {
                                    setNumPages(0);
                                    setHasError(true);
                                }}
                            >
                                <PdfPage
                                    pageNumber={page}
                                    scale={scale ?? 1}
                                    className="tw-max-w-full"
                                    onLoadSuccess={(loadedPage) =>
                                        setPageWidth(loadedPage.getViewport({ scale: 1 }).width)
                                    }
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                />
                            </PdfDocument>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentPreview;
